import axios from 'axios'
import { validateDeviceId } from '../../shared/utils/deviceIdGenerator.js'
import { USE_JWT_COOKIE } from '../../shared/auth/authConfig.js'

/**
 * APIベースURL
 * モバイル認証用のAPIエンドポイント
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/pix/api'

/**
 * モバイル認証用のaxiosインスタンス
 * 共通の設定とインターセプターが適用されたHTTPクライアント
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: USE_JWT_COOKIE,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * 共通のRequest interceptor関数
 * APIリクエスト時に認証トークンを自動付与（cookie モードでは付与しない）
 * @param {string} serviceName - サービス名（ログ用）
 * @returns {Function} interceptor関数
 */
export const createMobileRequestInterceptor = () => {
  return (config) => {
    // ネイティブアプリから保存されたUUID/DeviceIdをヘッダーに付与
    const nativeUuid = localStorage.getItem('nativeUuid')
    const nativeDeviceId = localStorage.getItem('nativeDeviceId')
    if (nativeUuid) {
      config.headers['User-Key'] = nativeUuid
    }
    if (nativeDeviceId) {
      config.headers['Device-Id'] = nativeDeviceId
    }
    if (USE_JWT_COOKIE) return config
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  }
}

/**
 * 共通のResponse interceptor関数（成功時）
 * APIレスポンスの成功時の処理
 * @param {string} serviceName - サービス名（ログ用）
 * @returns {Function} interceptor関数
 */
export const createMobileResponseInterceptor = () => {
  return (response) => {
    return response
  }
}

/**
 * 共通のResponse interceptor関数（エラー時）
 * APIレスポンスのエラー時の処理
 * @param {string} serviceName - サービス名（ログ用）
 * @returns {Function} interceptor関数
 */
export const createMobileErrorInterceptor = () => {
  return (error) => {
    return Promise.reject(error)
  }
}

/**
 * 共通のinterceptorを設定する関数
 * axiosインスタンスにリクエスト・レスポンスインターセプターを設定
 * @param {Object} axiosInstance - axiosインスタンス
 * @param {string} serviceName - サービス名（ログ用）
 */
export const setupMobileInterceptors = (axiosInstance, serviceName = 'Mobile') => {
  // Request interceptor
  axiosInstance.interceptors.request.use(
    createMobileRequestInterceptor(serviceName),
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor
  axiosInstance.interceptors.response.use(
    createMobileResponseInterceptor(serviceName),
    createMobileErrorInterceptor(serviceName)
  )
}

// mobileAuthService用のインターセプターを設定
setupMobileInterceptors(apiClient, 'Mobile Auth')

/**
 * モバイル認証サービス
 * ログイン、ログアウト、トークン検証などの認証関連機能を提供
 */
export const mobileAuthService = {
  /**
   * モバイルログイン処理
   * メールアドレス、パスワード、デバイスIDを使用してログイン認証を行う
   * @param {Object} credentials - ログイン認証情報
   * @param {string} credentials.mailAddress - メールアドレス
   * @param {string} credentials.password - パスワード
   * @param {string} credentials.deviceId - デバイスID
   * @returns {Promise<Object>} ログイン結果
   */
  async login(credentials) {
    // Validate device ID for Amazon SNS compatibility
    if (!credentials.deviceId || !validateDeviceId(credentials.deviceId)) {
      throw new Error('デバイスIDが無効です。Amazon SNSの制限（400文字以下の16進数）を満たしていません。')
    }
    
    const response = await apiClient.post('/loginCheckPixtag', {
      mailAddress: credentials.mailAddress,
      loginPassword: credentials.password,
      deviceId: credentials.deviceId,
    })

    if (response.data.successFlg === '1') {
      // Store JWT token if available (cookie mode: no action; header mode: localStorage)
      if (response.data.token) {
        if (!USE_JWT_COOKIE) {
          localStorage.setItem('token', response.data.token)
        }
      }
      // Update login time
      await this.updateLoginTime(response.data.userId)
    }

    return response.data
  },

  /**
   * ログイン時間の更新
   * ユーザーの最終ログイン時間をサーバーに記録
   * @param {string} userId - ユーザーID
   */
  async updateLoginTime(userId) {
    try {
      await apiClient.post('/updatePixUserLoginTime', {
        pixUserId: userId,
      })
    } catch (error) {
      // ログイン時間の更新失敗は致命的ではないため、エラーを無視
    }
  },

  /**
   * JWTトークンの検証
   * 現在のJWTトークンが有効かどうかをサーバーに確認
   * @returns {Promise<Object>} トークン検証結果
   */
  async validateToken() {
    const response = await apiClient.get('/validateToken')
    return response.data
  },

  /**
   * ログアウト処理
   * トークンを削除（cookie モード時は API で cookie 削除を依頼）
   */
  async logout() {
    if (USE_JWT_COOKIE) {
      try {
        await apiClient.post('/logout')
      } catch (error) {
        // ignore
      }
    }
    localStorage.removeItem('token')
  },
}

export default apiClient
